import { Component, OnInit } from "@angular/core";
import { GridApi, GridOptions, GridReadyEvent, ValueFormatterParams } from "ag-grid-community";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { CustomNoRowsOverlayComponent } from "../../../shared/custom-no-rows-overlay/custom-no-rows-overlay.component";
import { CxoneEtlReportingSetService } from "../../cxone-etl-reporting-set.service";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { ETLBasefeed, ETLFeedDataList, SimpleFieldModalType } from "../../cxone-etl-reporting-set.model";
import { CxoneHighlightSearchCellRendererComponent } from 'src/app/reusables/ag-grid-components/cxone-highlight-search-cell-renderer/cxone-highlight-search-cell-renderer.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';

@Component({
    selector: "cv-add-simple-feed-modal",
    templateUrl: "./add-simple-feed-modal.component.html",
    styleUrls: ["./add-simple-feed-modal.component.scss"],
})
export class AddSimpleFeedModalComponent implements OnInit {
    public searchPlaceholderText: string;
    public feedCountLabel = "";
    public searchInput = "";
    public gridApi: GridApi;
    public filteredFeedGridData: any;
    public showSpinner = false;
    public headerTitle: string;
    public gridOptions: GridOptions;
    public advancedVisibility = false;
    public hideExtraColumns = true;
    public saveDisabled = true;
    public selectedRows: SimpleFieldModalType[];

    constructor(
        private dialogRef: DynamicDialogRef,
        private cvLocalizationService: CVLocalizationService,
        private cxoneEtlReportingSet: CxoneEtlReportingSetService,
        private dialogConfig: DynamicDialogConfig,
        private agGridHelperService: AgGridHelperService,
    ) { }

    async ngOnInit(): Promise<void> {
        const joinedFeedsList = this.dialogConfig.data?.joinedFeedsList;
        if (joinedFeedsList && joinedFeedsList.length > 0) {
            this.hideExtraColumns = false;
        }
        await this.loadLocalization();
        await this.setGridOption();
    }

    async setGridOption() {
        this.gridOptions = {
            columnDefs: [
                {
                    headerCheckboxSelection: true,
                    checkboxSelection: true,
                    suppressMovable: true,
                    headerClass: ["checkBoxClass", "customFieldClass"],
                    cellClass:"customFieldCellClass",
                    width: 60
                },
                {
                    field: "FIELDS",
                    suppressMovable: true,
                    suppressSizeToFit: !this.hideExtraColumns,
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    valueFormatter: (params: ValueFormatterParams) => params.data?.FIELDS?.toString(),
                },
                {
                    field: "SOURCE",
                    suppressMovable: true,
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    hide: this.hideExtraColumns
                },
                {
                    field: "FEED NAME",
                    suppressMovable: true,
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    hide: this.hideExtraColumns
                },
                { 
                    field: "DATA TYPE",
                    suppressMovable: true,
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    valueFormatter: (params: ValueFormatterParams) => params.data['DATA TYPE']?.toString(),
                },
                { 
                    field: "JOINNAME",
                    suppressMovable: true,
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    hide: true
                },
                { 
                    field: "JOINID",
                    suppressMovable: true,
                    cellRenderer: 'cxoneHighlightSearchRenderer',
                    hide: true
                },
            ],
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            onRowDataUpdated: () => this.setRowCount(),
            onFilterChanged: () => {
                this.gridApi.redrawRows();
                this.setRowCount();
            },
            components: {
                CustomNoRowsOverlayComponent,
                cxoneHighlightSearchRenderer: CxoneHighlightSearchCellRendererComponent
            },
            noRowsOverlayComponent: "CustomNoRowsOverlayComponent",
            scrollbarWidth: 8,
            noRowsOverlayComponentParams: {
                noRowsOverlayTextFunc: () =>
                    this.searchInput !== ""
                        ? this.getLocalizationString(
                            "No_Result_Found",
                            "No result found"
                        )
                        : this.getLocalizationString(
                            "No_Feed_Added",
                            "No feed added yet"
                        ),
                noRowsOverlayIconPathFunc: () => {
                    const path =
                        this.searchInput !== ""
                            ? "../App_Themes/CXone/icons/no-results.svg#no-results"
                            : "../App_Themes/CXone/icons/empty-state.svg#empty-state";
                    return path;
                },
            },
            isExternalFilterPresent: () => true,
            doesExternalFilterPass: (node) => {
                const result = this.agGridHelperService.doesExternalFilterPassIncludeHidden(node, this.searchInput);
                return result;
            },
            context: this,
            onRowSelected: this.onRowSelected.bind(this),
        };
    }

    async onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.gridApi.setGridOption('columnDefs', this.gridOptions.columnDefs);
        this.advancedVisibility = this.dialogConfig.data.advancedVisibility || false;
        const baseFeed = this.dialogConfig.data.baseFeed;
        const dataType = this.dialogConfig.data.dataType;
        await this.loadData(baseFeed, dataType);
        this.gridApi.sizeColumnsToFit();
    }

    async loadData(baseFeed: string, dataType: string) {
        const joinedFeedsList = this.dialogConfig.data?.joinedFeedsList;
        const response = await this.cxoneEtlReportingSet.loadBasefeed();
        const dataDef = response
            .filter((data: ETLBasefeed) => data.FeedName === baseFeed)
            .pop();
        const dataList = dataDef.DataList;
        let rowData = dataList.map((data: ETLFeedDataList) => {
            return {
                FIELDS: data.Name,
                SOURCE: dataDef.SourceSystem,
                "FEED NAME": dataDef.FeedName,
                "DATA TYPE": this.getDataType(data.DataType),
                JOINNAME: '',
                JOINID: -1
            };
        });

        if (joinedFeedsList) {
            joinedFeedsList.forEach((e, index:number) => {
                if (e.feed2 && e.feed2.FeedName) {
                    let feed = response.find(item => item.FeedName === e.feed2.FeedName);
                    if (feed && feed.DataList) {
                        let dataList = feed.DataList.map((data: ETLFeedDataList) => ({
                            FIELDS: data.Name,
                            SOURCE: feed.SourceSystem,
                            'FEED NAME': feed.FeedName,
                            'DATA TYPE': this.getDataType(data.DataType),
                            JOINNAME: e.joinedFeedName,
                            JOINID : index
                        }));
                        rowData = [...rowData, ...dataList];
                    }
                }
            }); 
        }

        if (dataType) rowData = rowData.filter((item) => item["DATA TYPE"] === dataType);
        rowData = this.removeDuplicateRecords(rowData);
        rowData = rowData.sort((a, b) => a.FIELDS.localeCompare(b.FIELDS));
        this.filteredFeedGridData = [...rowData];
        this.gridApi.setRowData(this.filteredFeedGridData);
    }

    removeDuplicateRecords(arr) {
        const unique = [];
        const keys = new Set();
    
        arr.forEach(item => {
            const key = `${item['FIELDS']}_${item['FEED NAME']}`;
    
            if (!keys.has(key)) {
                keys.add(key);
                unique.push(item);
            }
        });
    
        return unique;
    }

    getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText(
            "ETL_Reporting_Set",
            key,
            defaultValue
        );
        return baseString;
    }

    onRowSelected() {
        this.selectedRows = this.gridApi.getSelectedRows();
        if (this.selectedRows.length > 0) {
            if (this.saveDisabled) this.saveDisabled = false;
        } else {
            this.saveDisabled = true;
        }
    }

    getDataType(dataType: number): string {
        let convertedDataType = "";
        switch (dataType) {
            case 0:
                convertedDataType = "String";
                break;
            case 1:
                convertedDataType = "Integer";
                break;
            case 2:
                convertedDataType = "Floating-Point";
                break;
            case 3:
                convertedDataType = "Date";
                break;
            case 4:
                convertedDataType = "Boolean";
                break;
        }

        return convertedDataType;
    }

    userSearchQueryChange(inputText: string) {
        this.searchInput = inputText;
        this.gridApi.onFilterChanged();
        if (this.gridApi?.getDisplayedRowCount() === 0) {
            this.gridApi.showNoRowsOverlay();
        } else {
            this.gridApi?.hideOverlay();
        }
    }

    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection(
                "ETL_Reporting_Set"
            );
        } catch {
            console.log(
                "Failed to load localization for ETL_Reporting_Set"
            );
        } finally {
            this.searchPlaceholderText = this.getLocalizationString(
                "Search",
                "Search fields"
            );
            let modalTitle = this.dialogConfig.data.headerTitle || ["Add_Simple_Fields", "Simple Fields"];
            this.headerTitle = this.getLocalizationString(modalTitle[0], modalTitle[1]);
        }
    }

    setRowCount() {
        let fieldTxt = this.gridApi.getDisplayedRowCount() == 1 ? 
                            this.getLocalizationString("Total_Fields_Singular", "field"):
                            this.getLocalizationString("Total_Fields_Plural", "fields");
        this.feedCountLabel = `${this.gridApi.getDisplayedRowCount()} ${fieldTxt}`;
    }

    cancel(): void {
        this.dialogRef.close();
    }

    async save(): Promise<void> {
        this.dialogRef.close(this.selectedRows);
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    advance(): void {
        this.dialogRef.close(true);
    }
}

